import { useMutation } from "@tanstack/react-query";

import { gql, GraphQLAPI, GraphQLResult, queries } from "@examspark/api";
import {
  assertAppSyncError,
  getStableClassroomStudentId,
  hasConditionalCheckFailedException,
} from "@examspark/common";
import {
  CreateStudentInput,
  DeleteStudentInput,
  ModelStudentConnection,
  UpdateStudentInput,
} from "@examspark/types";

export const useMutationCreateStudent = () => {
  return useMutation({
    mutationFn: async (params: {
      classroomId: string;
      studentId: string;
      service?: "google-classroom";
      input: Omit<CreateStudentInput, "sk">;
    }) => {
      const id = getStableClassroomStudentId(
        params.classroomId,
        params.studentId,
        {
          service: params.service,
        },
      );

      try {
        return await GraphQLAPI.query({
          query: queries.classroom.createStudent,
          variables: {
            input: {
              ...params.input,
              sk: `student#${id}`,
            },
          },
        });
      } catch (error) {
        assertAppSyncError(error);
        if (hasConditionalCheckFailedException(error)) {
          throw new Error(
            "A student with this ID already exists in this classroom",
          );
        }

        throw error;
      }
    },
  });
};

export const useMutationCreateClassroomStudentAction = () => {
  return useMutation({
    mutationFn: async (input: {
      workspaceId: string;
      classroomId: string;
      studentId: string; // local student id
      firstName: string;
      lastName: string;
      assignmentAccessCode?: string;
      sisRosterStudentId?: string;
    }) => {
      const result = (await GraphQLAPI.query({
        query: /* GraphQL */ `
          mutation CreateClassroomStudentAction($input: ControllerInput!) {
            createClassroomStudentAction(input: $input)
          }
        `,
        variables: {
          input: {
            action: "createClassroomStudent",
            params: JSON.stringify(input),
          },
        },
      })) as any;

      return result.data.createClassroomStudentAction;
    },
  });
};

export const useMutationDeleteStudent = () => {
  return useMutation({
    mutationFn: async (input: DeleteStudentInput) => {
      return await GraphQLAPI.query({
        query: queries.classroom.deleteStudent,
        variables: {
          input,
        },
      });
    },
  });
};

export const useMutationUpdateStudent = () => {
  return useMutation({
    mutationFn: async (input: UpdateStudentInput) => {
      return await GraphQLAPI.query({
        query: queries.classroom.updateStudent,
        variables: {
          input,
        },
      });
    },
  });
};

export const useMutationUpdateClassroomStudentAction = () => {
  return useMutation({
    mutationFn: async (input: {
      id: string;
      sk: string;
      workspaceId: string;
      classroomId: string;
      studentId: string;
      firstName: string;
      lastName: string;
      assignmentAccessCode?: string;
      sisRosterStudentId?: string;
    }) => {
      const result = (await GraphQLAPI.query({
        query: /* GraphQL */ `
          mutation UpdateClassroomStudentAction($input: ControllerInput!) {
            updateClassroomStudentAction(input: $input)
          }
        `,
        variables: {
          input: {
            action: "updateClassroomStudent",
            params: JSON.stringify(input),
          },
        },
      })) as any;

      return result.data.updateClassroomStudentAction;
    },
  });
};

export const useMutationListClassroomStudents = () => {
  return useMutation({
    mutationFn: async (input: { workspaceId: string; classroomId: string }) => {
      const result = (await GraphQLAPI.query<{
        listClassroomStudents: ModelStudentConnection;
      }>({
        query: queries.classroom.listClassroomStudents,
        variables: {
          input: {
            id: input.classroomId,
            sk: {
              beginsWith: "#",
            },
            workspaceId: input.workspaceId,
          },
        },
      })) as GraphQLResult<{
        listClassroomStudents: ModelStudentConnection;
      }>;

      return result.data?.listClassroomStudents.items;
    },
  });
};

export const useMutationGetClassroomStudent = () => {
  return useMutation({
    mutationFn: (params: { classroomId?: string; studentId?: string }) => {
      return gql.operations.classroom.getClassroomStudent({
        id: params.classroomId!,
        sk: `student#${params.studentId!}`,
      });
    },
  });
};
